import { useReducer, useMemo, useCallback, useState } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  format,
  parse,
  startOfWeek as dateFnsStartOfWeek,
  getDay,
  addWeeks,
  addDays,
} from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import { toast } from 'sonner';
import { useMutation } from 'convex/react';
import { Id } from '../../convex/_generated/dataModel';
import type { NavigateAction } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../styles/calendar.css';
import { useScheduledPosts } from '../hooks/useScheduledPosts';
import { ICalendarState, CalendarAction, IRBCEvent, ICalendarEvent } from '../@types/calendar';
import { api } from '../../convex/_generated/api';
import EventCard from '../components/calendar/EventCard';
import RescheduleDialog from '../components/calendar/RescheduleDialog';
import { CustomToolbarMemo } from '../components/calendar/CustomToolbar';
import { PostWriterSubmenuMemo } from '../components/post-writer/PostWriterSubmenu';

// Setup date-fns localizer for react-big-calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => dateFnsStartOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

// Wrap Calendar with drag-and-drop support
const DnDCalendar = withDragAndDrop<IRBCEvent, object>(Calendar);

// Calendar state reducer
const initialState: ICalendarState = {
  currentDate: new Date(),
  view: 'week',
  timeRange: {
    start: 6, // 6:00 AM
    end: 22, // 10:00 PM
  },
  selectedEvent: null,
  isDragging: false,
};

function calendarReducer(state: ICalendarState, action: CalendarAction): ICalendarState {
  switch (action.type) {
    case 'SET_DATE':
      return { ...state, currentDate: action.payload };
    case 'NEXT_WEEK':
      return { ...state, currentDate: addWeeks(state.currentDate, 1) };
    case 'PREV_WEEK':
      return { ...state, currentDate: addWeeks(state.currentDate, -1) };
    case 'NEXT_DAY':
      return { ...state, currentDate: addDays(state.currentDate, 1) };
    case 'PREV_DAY':
      return { ...state, currentDate: addDays(state.currentDate, -1) };
    case 'SET_VIEW':
      return { ...state, view: action.payload };
    case 'SELECT_EVENT':
      return { ...state, selectedEvent: action.payload };
    case 'START_DRAG':
      return { ...state, isDragging: true };
    case 'END_DRAG':
      return { ...state, isDragging: false };
    case 'SET_TIME_RANGE':
      return { ...state, timeRange: action.payload };
    default:
      return state;
  }
}

export const CalendarPage = () => {
  // ============================================
  // 1. HOOKS
  // ============================================
  const [state, dispatch] = useReducer(calendarReducer, initialState);
  const { rbcEvents, isLoading } = useScheduledPosts(state.currentDate);

  const [selectedEvent, setSelectedEvent] = useState<ICalendarEvent | null>(null);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const reschedulePost = useMutation(api.socialMedia.scheduledPublishing.reschedulePost);

  // ============================================
  // 2. HELPER FUNCTIONS (with useCallback)
  // ============================================
  const handleToolbarNavigate = useCallback(
    (action: NavigateAction) => {
      if (action === 'TODAY') {
        dispatch({ type: 'SET_DATE', payload: new Date() });
      } else if (action === 'PREV') {
        if (state.view === 'day') {
          dispatch({ type: 'PREV_DAY' });
        } else {
          dispatch({ type: 'PREV_WEEK' });
        }
      } else if (action === 'NEXT') {
        if (state.view === 'day') {
          dispatch({ type: 'NEXT_DAY' });
        } else {
          dispatch({ type: 'NEXT_WEEK' });
        }
      }
    },
    [state.view]
  );

  const handleCalendarNavigate = useCallback(
    (newDate: Date, _view: View, action: NavigateAction) => {
      if (action === 'TODAY' || action === 'PREV' || action === 'NEXT') {
        handleToolbarNavigate(action);
      } else {
        dispatch({ type: 'SET_DATE', payload: newDate });
      }
    },
    [handleToolbarNavigate]
  );

  const handleViewChange = useCallback((view: View) => {
    dispatch({ type: 'SET_VIEW', payload: view as 'week' | 'day' });
  }, []);

  const handleSelectEvent = useCallback((event: IRBCEvent) => {
    setSelectedEvent(event.resource);
    setShowRescheduleDialog(true);
  }, []);

  const handleEventDrop = useCallback(
    async (args: { event: IRBCEvent; start: string | Date; end: string | Date }) => {
      const { event, start } = args;
      const calendarEvent = event.resource;

      // Convert start to Date if it's a string
      const startDate = typeof start === 'string' ? new Date(start) : start;

      // Validation: Only allow rescheduling scheduled posts
      if (calendarEvent.status !== 'scheduled') {
        toast.error(`Cannot reschedule ${calendarEvent.status} posts`);
        return;
      }

      // Validation: Prevent moving to past times
      if (startDate.getTime() < Date.now()) {
        toast.error('Cannot schedule posts in the past');
        return;
      }

      try {
        await reschedulePost({
          scheduledPostId: calendarEvent.id as Id<'scheduledPosts'>,
          newScheduledFor: startDate.getTime(),
        });
        toast.success('Post rescheduled successfully');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reschedule post';
        toast.error(message);
      }
    },
    [reschedulePost]
  );

  const handleCloseDialog = useCallback(() => {
    setShowRescheduleDialog(false);
    setSelectedEvent(null);
  }, []);

  const handleRescheduleSuccess = useCallback(() => {
    // Dialog will close itself, just reset state
    setSelectedEvent(null);
  }, []);

  // ============================================
  // TOOLBAR LABEL
  // ============================================
  const getToolbarLabel = useCallback(() => {
    const { currentDate, view } = state;

    if (view === 'week') {
      const start = dateFnsStartOfWeek(currentDate, { weekStartsOn: 1 });
      const end = addWeeks(start, 1);
      return `${format(start, 'MMMM dd')} – ${format(end, 'dd, yyyy')}`;
    } else if (view === 'day') {
      return format(currentDate, 'MMMM dd, yyyy');
    } else {
      return format(currentDate, 'MMMM yyyy');
    }
  }, [state]);

  // ============================================
  // 3. COMPUTED VALUES
  // ============================================
  const minTime = useMemo(
    () => new Date(1970, 0, 1, state.timeRange.start, 0, 0),
    [state.timeRange.start]
  );
  const maxTime = useMemo(
    () => new Date(1970, 0, 1, state.timeRange.end, 0, 0),
    [state.timeRange.end]
  );

  // ============================================
  // 4. EVENT STYLING
  // ============================================
  const eventStyleGetter = useCallback((event: { resource: { status: string } }) => {
    const status = event.resource.status;

    const statusColors: Record<string, { backgroundColor: string; borderColor: string }> = {
      scheduled: { backgroundColor: '#dbeafe', borderColor: '#3b82f6' },
      published: { backgroundColor: '#dcfce7', borderColor: '#22c55e' },
      failed: { backgroundColor: '#fee2e2', borderColor: '#ef4444' },
      publishing: { backgroundColor: '#fef9c3', borderColor: '#eab308' },
      cancelled: { backgroundColor: '#f3f4f6', borderColor: '#9ca3af' },
    };

    const colors = statusColors[status] || statusColors.scheduled;

    return {
      style: {
        backgroundColor: colors.backgroundColor,
        borderLeft: `3px solid ${colors.borderColor}`,
        borderRadius: '4px',
        opacity: 0.95,
        padding: '2px',
        margin: '1px 0',
      },
    };
  }, []);

  // ============================================
  // 5. CUSTOM COMPONENTS
  // ============================================
  const CustomEvent = useCallback(({ event }: { event: IRBCEvent }) => {
    return <EventCard event={event.resource} />;
  }, []);

  // Calendar components
  const calendarComponents = useMemo(
    () => ({
      event: CustomEvent,
      toolbar: () => null,
    }),
    [CustomEvent]
  );

  // ============================================
  // 6. COMPUTED CONTENT
  // ============================================
  const loadingContent = (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        <p className="mt-4 text-muted-foreground">Loading calendar...</p>
      </div>
    </div>
  );

  const calendarContent = (
    <div className="flex h-screen flex-col">
      {/* Page Header */}
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-3xl font-bold">Scheduled Posts</h1>
      </div>

      {/* Calendar Container */}
      <div className="flex-1 flex flex-col overflow-hidden mx-6 mb-6">
        <div className="flex flex-col border rounded-lg bg-card overflow-hidden">
          {/* Custom Toolbar - stays full width, outside scroll container */}
          <CustomToolbarMemo
            label={getToolbarLabel()}
            onNavigate={handleToolbarNavigate}
            onView={handleViewChange}
            view={state.view}
          />

          {/* Calendar Content - only this scrolls horizontally */}
          <div className="flex-1 overflow-auto">
            <div className={state.view === 'week' ? 'min-w-[1400px] h-full' : 'h-full w-full'}>
              <DnDCalendar
                localizer={localizer}
                events={rbcEvents}
                startAccessor="start"
                endAccessor="end"
                date={state.currentDate}
                defaultView="week"
                view={state.view}
                views={['week', 'day']}
                onView={handleViewChange}
                onNavigate={handleCalendarNavigate}
                onSelectEvent={handleSelectEvent}
                onEventDrop={handleEventDrop}
                onEventResize={() => {}}
                resizable={false}
                draggableAccessor={(event: IRBCEvent) => event.resource.isDraggable}
                eventPropGetter={eventStyleGetter}
                components={calendarComponents}
                step={60}
                timeslots={1}
                min={minTime}
                max={maxTime}
                style={{ height: '100%' }}
                formats={{
                  timeGutterFormat: 'HH:mm',
                  eventTimeRangeFormat: () => '',
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reschedule Dialog */}
      <RescheduleDialog
        event={selectedEvent}
        isOpen={showRescheduleDialog}
        onClose={handleCloseDialog}
        onSuccess={handleRescheduleSuccess}
      />
    </div>
  );

  // ============================================
  // 7. DETERMINE CONTENT TO DISPLAY
  // ============================================
  const mainContent = isLoading ? loadingContent : calendarContent;

  // ============================================
  // 8. RETURN STRUCTURE
  // ============================================
  return {
    submenu: <PostWriterSubmenuMemo />,
    mainContent,
  };
};
